import { createAction, createSlice, PayloadAction } from "@reduxjs/toolkit";
import type { DocumentSnapshot } from "../documentState";
import type { HistoryEntry, HistoryFrame } from "../historyState";

export type HistorySnapshot = DocumentSnapshot;

export interface HistoryState {
  past: HistoryEntry[];
  future: HistoryEntry[];
  savedCheckpoint: HistorySnapshot | null;
  documentDirty: boolean;
  externalDirty: boolean;
  transactionActive: boolean;
}

export const HISTORY_LIMIT = 50;

const initialState: HistoryState = {
  past: [],
  future: [],
  savedCheckpoint: null,
  documentDirty: false,
  externalDirty: false,
  transactionActive: false,
};

type CommitHistoryPayload = {
  entry: HistoryEntry;
  documentDirty: boolean;
};

export const historySlice = createSlice({
  name: "history",
  initialState,
  reducers: {
    addToHistory: (state, action: PayloadAction<HistoryEntry>) => {
      state.past.push(action.payload);
      if (state.past.length > HISTORY_LIMIT) state.past.shift();
      state.future = [];
    },
    commitHistory: (state, action: PayloadAction<CommitHistoryPayload>) => {
      state.past.push(action.payload.entry);
      if (state.past.length > HISTORY_LIMIT) state.past.shift();
      state.future = [];
      state.documentDirty = action.payload.documentDirty;
    },
    clearHistory: (state) => {
      state.past = [];
      state.future = [];
    },
    resetHistory: () => initialState,
    setSavedCheckpoint: (state, action: PayloadAction<HistorySnapshot>) => {
      state.savedCheckpoint = action.payload;
      state.documentDirty = false;
      state.externalDirty = false;
    },
    rebaseHistory: (state, action: PayloadAction<HistorySnapshot>) => {
      state.past = [];
      state.future = [];
      state.savedCheckpoint = action.payload;
      state.documentDirty = false;
      state.externalDirty = false;
      state.transactionActive = false;
    },
    markExternalDirty: (state) => {
      state.externalDirty = true;
    },
    clearExternalDirty: (state) => {
      state.externalDirty = false;
    },
    setTransactionActive: (state, action: PayloadAction<boolean>) => {
      state.transactionActive = action.payload;
    },
  },
});

export const {
  addToHistory,
  commitHistory,
  clearHistory,
  resetHistory,
  setSavedCheckpoint,
  rebaseHistory,
  markExternalDirty,
  clearExternalDirty,
  setTransactionActive,
} = historySlice.actions;

export const undo = createAction("history/undo");
export const redo = createAction("history/redo");
export const restoreHistoryFrame = createAction<HistoryFrame>(
  "history/restoreFrame"
);
export const markCurrentDocumentSaved = createAction(
  "history/markCurrentDocumentSaved"
);
export const rebaseCurrentDocument = createAction(
  "history/rebaseCurrentDocument"
);

export type HistoryTransactionPayload = { id: string };
export const beginHistoryTransaction = createAction<HistoryTransactionPayload>(
  "history/beginTransaction"
);
export const commitHistoryTransaction = createAction<HistoryTransactionPayload>(
  "history/commitTransaction"
);
export const cancelHistoryTransaction = createAction<HistoryTransactionPayload>(
  "history/cancelTransaction"
);

export const selectCanUndo = (state: { history: HistoryState }) =>
  !state.history.transactionActive && state.history.past.length > 0;
export const selectCanRedo = (state: { history: HistoryState }) =>
  !state.history.transactionActive && state.history.future.length > 0;
export const selectDocumentIsDirty = (state: { history: HistoryState }) =>
  state.history.documentDirty || state.history.externalDirty;
export const selectSavedCheckpoint = (state: { history: HistoryState }) =>
  state.history.savedCheckpoint;

export default historySlice.reducer;
